import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import {
  Address,
  CampaignTariffSelectionData,
  IConfirmationStatus,
  IdCard,
  IERechnungsInfo,
  INewClientTrackingData,
  IOlavEntry,
  IOrderData,
  IOrderDetailsInfo,
  IPersonalData,
  IProduktAuswahl,
  IProduktDetails,
  IProduktSelektion,
  IProduktVerfuegbarkeit,
  IUploadResult,
  IVertragskonto,
  KYC_FAILURE_STATUSES,
  KYC_MAX_POLL_ERRORS,
  KYC_POLLING_STATUSES,
  KYC_STATUS_POLL_INTERVAL_MS,
  KycResult,
  KycStatus,
  OrderIPDFLinks,
  PreisIndikation,
  Product,
  RecentOrderType,
} from '@mwe/models';
import { BehaviorSubject, catchError, EMPTY, exhaustMap, finalize, from, of, pipe, switchMap, takeWhile, tap, timer } from 'rxjs';
import { ProductCategoryEnum, ServiceStateEnum } from '@mwe/constants';
import { INewVoucherFormConfig } from '../../voucher/new-voucher/new-voucher.model';
import { withStorageServiceFeature } from '../../store-features/storage-service.feature';
import { withAuthenticationStateFeature } from '../../store-features/authentication.feature';
import { ConfirmationStatusState, withConfirmationStatusState } from '../../store-features/confirmation-status-state.feature';
import { MemoryCacheItem } from '../../cache/memory-cache.service';
import { computed, inject, Injector } from '@angular/core';
import { TariffSelectionStateService } from '../../tariff/selection/tariff-selection-state.service';
import { isProductCategory, sortProductsByCategory } from '@mwe/utils';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { KycService } from '../../kyc/kyc.service';
import { withResource } from '@angular-architects/ngrx-toolkit';
import { InternetDocumentUrlService } from './internet-document-url.service';

const STORAGE_KEY = 'internet';

const initialState = {
  internetProfileChecked: undefined as boolean,
  newProductAvailabilitiesSuccessStateSubject: undefined as BehaviorSubject<true>,
  submitSuccessStateSubject: undefined as BehaviorSubject<ServiceStateEnum.INIT>,
  internetAddress: undefined as Address,
  internetAddressOlav: undefined as IOlavEntry,
  invoiceAddressOlav: undefined as IOlavEntry,
  newProductAvailabilities: undefined as IProduktVerfuegbarkeit[],
  newProductAvailabilitiesState: undefined as ServiceStateEnum,
  tariffSelectionState: undefined as ServiceStateEnum,
  predefinedProductsForSelection: undefined as IProduktAuswahl,
  productsForSelection: undefined as IProduktAuswahl,
  productSelection: undefined as IProduktSelektion,
  selectedPriceIndications: undefined as PreisIndikation[],
  registrationDate: undefined as string,
  orderDetails: undefined as IOrderDetailsInfo[],
  personalData: undefined as IPersonalData,
  invoiceData: undefined as IVertragskonto,
  idCard: undefined as IdCard,
  uploadedFiles: undefined as IUploadResult,
  pdfLinks: undefined as OrderIPDFLinks,
  eRechnungsInfo: undefined as IERechnungsInfo,
  lastOrderDate: undefined as string,
  orderSuccessfull: undefined as boolean,
  submitSuccessState: undefined as ServiceStateEnum,
  trackingData: undefined as INewClientTrackingData,
  sendProductInterestState: undefined as ServiceStateEnum,
  grantedMarketingPermission: undefined as boolean,
  skipPreselectedAddress: undefined as boolean,
  confirmationStatus: undefined as IConfirmationStatus,
  previousSelectedProductCategories: [] as ProductCategoryEnum[],
  productsToShow: [] as Product[],
  predefinedProducts: [] as Product[],
  selectedProducts: [] as Product[],
  primaryCategorySelected: {} as Record<string, boolean>, //TODO: remove this it doesnt make sense to have a map in internet context right now because only one primary product is possible anyways
  campaignData: undefined as CampaignTariffSelectionData,
  newVoucherConfig: undefined as INewVoucherFormConfig,
  selectedCategories: undefined as ProductCategoryEnum[],
  initialEmail: undefined as string,

  // values from params
  predefinedOlavGaa: undefined as string,
  predefinedOlavAaa: undefined as string,
  predefinedTariffKey: undefined as string,
  predefinedTkConId: undefined as string,

  // if values from params are avlid the real values are set here
  tkConId: undefined as string,
  olavGaa: undefined as string,

  kycCheckStarted: undefined as boolean,
  kycFieldsReadOnly: undefined as boolean,
  kycResult: undefined as KycResult,
  kycStatus: undefined as KycStatus,
  kycUrl: undefined as string,
  kycIsPolling: undefined as boolean,
  kycPollError: undefined as string,
  kycStartAttempts: undefined as number,
  /** true while the KYC identity check popup/tab is open */
  kycPopupOpen: undefined as boolean,

  // recentOrderType
  orderType: undefined as RecentOrderType,
};

type InitialInternetState = typeof initialState;

export const InternetStore = signalStore(
  { providedIn: 'root' },
  withConfirmationStatusState({
    _memoryCacheItem: MemoryCacheItem.ClearingAccountDetails,
  }),
  withState(initialState),
  withStorageServiceFeature(STORAGE_KEY, true),
  withAuthenticationStateFeature(),
  withProps(() => ({
    _internetDocumentURLService: inject(InternetDocumentUrlService),
  })),
  withComputed(store => {
    const selectedPrimaryCategory = computed(() => store.selectedProducts()?.[0]?.category);
    const _tariffKey = computed(
      () => store.productSelection()?.internet?.tarif?.ISUTarifKey ?? store.productSelection()?.['mobiles internet']?.tarif?.ISUTarifKey,
    );
    const tvKey = computed(
      () =>
        store.productSelection()?.internet?.tarif?.tarifOptionen?.[0]?.option ??
        store.productSelection()?.['mobiles internet']?.tarif?.tarifOptionen?.[0]?.option ??
        '',
    );
    const isMvno = computed(() => isProductCategory(selectedPrimaryCategory(), ProductCategoryEnum?.['MOBILES INTERNET']));

    return {
      selectedPrimaryCategory,
      _tariffKey,
      tvKey,
      isMvno,
    };
  }),
  withResource(store => ({
    internetDocuments: store._internetDocumentURLService.getInternetDocumentUrls(store._tariffKey, store.tvKey, store.isMvno),
  })),
  withComputed(store => ({
    contractSummaryUrl: computed(() => store.internetDocumentsValue()?.contractSummaryUrl ?? ''),
    agbUrl: computed(() => store.internetDocumentsValue()?.agbUrl ?? ''),
    eblbUrl: computed(() => store.internetDocumentsValue()?.ebLbUrl ?? ''),
    eblbUrlTV: computed(() => store.internetDocumentsValue()?.ebLbTvUrl ?? ''),
    ruecktrittsbelehrung: computed(() => store.internetDocumentsValue()?.ruecktrittsbelehrungUrl ?? ''),
  })),
  withMethods(store => {
    const tariffSelectionStateService = inject(TariffSelectionStateService);
    const injector = inject(Injector);
    const updateStoreState = (storeStateValues: Partial<InitialInternetState> & Partial<ConfirmationStatusState>) => {
      const nextStateValues =
        'predefinedProducts' in storeStateValues
          ? {
              ...storeStateValues,
              predefinedProducts: sortProductsByCategory(
                [...(storeStateValues.predefinedProducts ?? [])].filter(
                  (product, index, self) => index === self.findIndex(p => p.category === product.category),
                ),
                true,
              ),
            }
          : { ...storeStateValues };

      patchState(store, nextStateValues);
    };
    const hydrateOrderDetails = (store, orderDetails) => {
      const pdi = { ...orderDetails } as IOrderDetailsInfo;
      const personalData = store.personalData();
      if (!pdi.eRechnungInfo) {
        pdi.eRechnungInfo = store.eRechnungsInfo();
      }
      if (!pdi.produktDetails) {
        pdi.produktDetails = {} as IProduktDetails;
      }
      if (personalData) {
        pdi.produktDetails.partner = {
          partnerId: null,
          partnerGruppe: null,
          nickname: null,
          registrierungStatus: null,
          rolle: null,
          partnerTyp: null,
          anrede: personalData.salutation,
          titel: personalData.title?.text,
          titelKurz: personalData.title?.value,
          nachname: personalData.lastName,
          vorname: personalData.firstName,
          firma: null,
          email: personalData.email,
          telefon: personalData.telefonNumber,
          telefonvorwahl: personalData.telefonCountryCode,
        };
        pdi.produktDetails.rechnungsempfaenger = {
          partnerId: null,
          partnerGruppe: null,
          nickname: null,
          registrierungStatus: null,
          rolle: null,
          partnerTyp: null,
          titel: personalData.title?.value,
          anrede: personalData.salutation,
          nachname: personalData.lastName,
          vorname: personalData.firstName,
          firma: null,
          email: personalData.email,
          telefon: personalData.telefonNumber,
          telefonvorwahl: personalData.telefonCountryCode,
          adresse: {
            ort: store.internetAddress().city,
            plz: store.internetAddress().postcode,
            strasse: store.internetAddress().street,
            hausnummer: store.internetAddress().streetNumber,
            raum: null,
            land: store.internetAddress().country,
            adresshinweis: null,
          },
        };
        pdi.partnerDetails = {
          adressen: null,
          bankkonten: null,
          geschaeftsPartnerId: null,
          kontaktdetails: [
            {
              email: { eMail: personalData.email },
              fax: null,
              telefon: { land: personalData.telefonCountryCode, nummer: personalData.telefonNumber },
            },
          ],
          kundensegment: null,
          nickname: null,
          organisation: null,
          systemId: null,
          physischePerson: {
            anrede: personalData.salutation,
            titel: personalData.title?.text,
            titelNachgestellt: null,
            titelVorgestellt: null,
            familienstand: null,
            geschlecht: null,
            staatsbuergerschaft: null,
            vorname: personalData.firstName,
            nachname: personalData.lastName,
            geburtsdatum: personalData.birthday + 'T00:00:00Z',
          },
        };
      }
      if (store.invoiceData()) {
        pdi.produktDetails.vertragskonto = store.invoiceData();
      }
      return pdi;
    };
    const getOrderData = (): IOrderData => {
      const orderDetails = store.orderDetails()?.map(od => hydrateOrderDetails(store, od));
      return {
        selectedOlav: store.internetAddressOlav(),
        newAddress: store.internetAddress(),
        registrationDate: store.registrationDate(),
        orderDetails: orderDetails,
        idCard: store.idCard(),
        uploadedFiles: store.uploadedFiles(),
        pdfLinks: store.pdfLinks(),
        priceIndications: tariffSelectionStateService.indications,
      };
    };

    const pollKycStatus = rxMethod<string | undefined>(
      pipe(
        switchMap(url => {
          if (!url) {
            return EMPTY;
          }
          patchState(store, { kycIsPolling: true, kycPollError: undefined });
          let consecutiveErrors = 0;
          return timer(0, KYC_STATUS_POLL_INTERVAL_MS).pipe(
            exhaustMap(() =>
              from(injector.get(KycService).getStatus()).pipe(
                tap(() => (consecutiveErrors = 0)),
                catchError(error => {
                  consecutiveErrors++;
                  patchState(store, { kycPollError: error?.message ?? 'KYC-Status Abfrage fehlgeschlagen' });
                  if (consecutiveErrors >= KYC_MAX_POLL_ERRORS) {
                    return of({ processStatus: KYC_FAILURE_STATUSES[0], result: null });
                  }
                  return EMPTY;
                }),
              ),
            ),
            tap(response => patchState(store, { kycStatus: response.processStatus, kycResult: response.result })),
            takeWhile(response => KYC_POLLING_STATUSES.includes(response.processStatus), true),
            finalize(() => patchState(store, { kycIsPolling: false })),
          );
        }),
      ),
    );

    return {
      updateStoreState,
      hydrateOrderDetails,
      getOrderData,
      pollKycStatus,
    };
  }),
  withHooks({
    onInit: store => {
      // whenever kycUrl gets set (KYC identity check started), automatically (re-)start the status polling
      store.pollKycStatus(store.kycUrl);
    },
  }),
);
